import { backgroundGradientList } from "../component/screenRecorder/RecorderBackgroundChooser";
import { logDebug, logError } from "../service/ServiceUtil";

let segmenter;
let segmenterPromise;
let depsPromise;
let bodySegmentation;
let mpScriptPromise;
let SelfieSegmentationCtor;

const MEDIAPIPE_SELFIE_SEGMENTATION_VERSION = "0.1.1675465747";
const MEDIAPIPE_SELFIE_SEGMENTATION_CDN =
  `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation@${MEDIAPIPE_SELFIE_SEGMENTATION_VERSION}`;

const getCanvasLike = (width, height) => {
  if (typeof OffscreenCanvas !== "undefined") {
    return new OffscreenCanvas(width, height);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

const getSourceWidth = (source) => source.width || source.videoWidth || 0;

const getSourceHeight = (source) => source.height || source.videoHeight || 0;

const segmentationMaskToImageData = (maskSource) => {
  const width = getSourceWidth(maskSource);
  const height = getSourceHeight(maskSource);
  const canvas = getCanvasLike(width, height);
  const context = canvas.getContext("2d", { willReadFrequently: true });
  context.drawImage(maskSource, 0, 0, width, height);
  return context.getImageData(0, 0, width, height);
};

const createSegmentationMask = (maskSource) => ({
  toCanvasImageSource: async () => maskSource,
  toImageData: async () => segmentationMaskToImageData(maskSource),
  getUnderlyingType: () => "canvasimagesource",
});

const resolveMediaPipeConstructor = () => {
  if (typeof globalThis === "undefined") return null;
  if (typeof globalThis.SelfieSegmentation === "function") {
    return globalThis.SelfieSegmentation;
  }
  return null;
};

const loadMediaPipeScript = async () => {
  const resolvedCtor = resolveMediaPipeConstructor();
  if (resolvedCtor) {
    SelfieSegmentationCtor = resolvedCtor;
    return;
  }
  if (!mpScriptPromise) {
    mpScriptPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(
        'script[data-mediapipe-selfie-segmentation="true"]',
      );
      if (existingScript) {
        if (resolveMediaPipeConstructor()) {
          resolve();
          return;
        }
        existingScript.addEventListener("load", resolve, { once: true });
        existingScript.addEventListener(
          "error",
          () => reject(new Error("Failed to load MediaPipe Selfie Segmentation script")),
          { once: true },
        );
        return;
      }

      const script = document.createElement("script");
      script.src = `${MEDIAPIPE_SELFIE_SEGMENTATION_CDN}/selfie_segmentation.js`;
      script.async = true;
      script.crossOrigin = "anonymous";
      script.dataset.mediapipeSelfieSegmentation = "true";
      script.onload = resolve;
      script.onerror = () =>
        reject(new Error("Failed to load MediaPipe Selfie Segmentation script"));
      document.head.appendChild(script);
    }).then(() => {
      const ctor = resolveMediaPipeConstructor();
      if (typeof ctor !== "function") {
        throw new Error("Failed to load MediaPipe SelfieSegmentation constructor");
      }
      SelfieSegmentationCtor = ctor;
    });
  }
  await mpScriptPromise;
};

const createMediaPipeSegmenter = async () => {
  const selfieSegmentation = new SelfieSegmentationCtor({
    locateFile: (file) =>
      `${MEDIAPIPE_SELFIE_SEGMENTATION_CDN}/${file}`,
  });
  let latestSegmentation = null;
  let selfieMode = false;

  selfieSegmentation.setOptions({
    modelSelection: 0,
    selfieMode,
  });
  selfieSegmentation.onResults((results) => {
    latestSegmentation = [
      {
        maskValueToLabel: () => "person",
        mask: createSegmentationMask(results.segmentationMask),
      },
    ];
  });

  if (typeof selfieSegmentation.initialize === "function") {
    await selfieSegmentation.initialize();
  }

  return {
    async segmentPeople(input, config = {}) {
      const nextSelfieMode = Boolean(config.flipHorizontal);
      if (nextSelfieMode !== selfieMode) {
        selfieMode = nextSelfieMode;
        selfieSegmentation.setOptions({ selfieMode });
      }
      await selfieSegmentation.send({ image: input });
      return latestSegmentation;
    },
    dispose() {
      if (typeof selfieSegmentation.close === "function") {
        selfieSegmentation.close();
      }
    },
  };
};

const initSegmentationDependencies = async () => {
  if (!depsPromise) {
    depsPromise = Promise.all([
      import("@tensorflow-models/body-segmentation"),
      loadMediaPipeScript(),
    ]).then(([bodySegmentationModule]) => {
      bodySegmentation =
        bodySegmentationModule.default || bodySegmentationModule;

      if (typeof SelfieSegmentationCtor !== "function") {
        throw new Error(
          "Failed to load MediaPipe SelfieSegmentation constructor",
        );
      }
    });
  }
  await depsPromise;
};

export const initSegmenter = async () => {
  if (segmenter) return segmenter;
  if (!segmenterPromise) {
    segmenterPromise = (async () => {
      await initSegmentationDependencies();
      segmenter = await createMediaPipeSegmenter();
      return segmenter;
    })().catch((e) => {
      segmenterPromise = null;
      segmenter = null;
      throw e;
    });
  }
  return await segmenterPromise;
};

export const waitUntilVideoIsReady = async (inputVideo) => {
  if (inputVideo.readyState < 2) {
    logDebug("video.readyState not ready", inputVideo.readyState);
    return await new Promise((resolve) => {
      inputVideo.onloadeddata = () => {
        logDebug("finally video.readyState", inputVideo.readyState);
        resolve(inputVideo);
      };
    });
  } else return inputVideo;
};

export const blurCameraBackground = async (inputVideo) => {
  try {
    await waitUntilVideoIsReady(inputVideo);
    await initSegmenter();
    if (segmenter?.segmentPeople) {
      const segmentation = await segmenter.segmentPeople(inputVideo);
      const offScreenCanvas = new OffscreenCanvas(
        inputVideo.videoWidth,
        inputVideo.videoHeight,
      );
      await bodySegmentation.drawBokehEffect(
        offScreenCanvas,
        inputVideo,
        segmentation,
        0.85 /* Foreground Threshold */,
        10 /* Blur Amount */,
        10 /* Edge Blur */,
      );
      return offScreenCanvas;
    } else logError("segmenter.segmentPeople not ready..");
  } catch (e) {
    logError("blurCameraBackground", {}, e);
    segmenter = null;
    segmenterPromise = null;
  }
};

export const replaceCameraBackground = async (
  inputVideo,
  backgroundGradientName,
) => {
  const foregroundColor = { r: 0, g: 255, b: 0, a: 255 };
  const backgroundColor = { r: 0, g: 0, b: 0, a: 0 };
  const drawContour = false;
  const foregroundThreshold = 0.99;
  try {
    await waitUntilVideoIsReady(inputVideo);
    await initSegmenter();
    if (segmenter?.segmentPeople) {
      const segmentation = await segmenter.segmentPeople(inputVideo, {
        flipHorizontal: false,
      });
      const binaryMask = await bodySegmentation.toBinaryMask(
        segmentation,
        foregroundColor,
        backgroundColor,
        drawContour,
        foregroundThreshold,
      );
      const maskCanvas = new OffscreenCanvas(
        binaryMask.width,
        binaryMask.height,
      );
      const maskCanvasContext = maskCanvas.getContext("2d", {
        alpha: true,
      });
      maskCanvasContext.putImageData(binaryMask, 0, 0);

      const compositeCanvas = new OffscreenCanvas(
        binaryMask.width,
        binaryMask.height,
      );
      const compositeCanvasContext = compositeCanvas.getContext("2d", {
        alpha: true,
      });
      compositeCanvasContext.filter = "blur(7px)";
      compositeCanvasContext.drawImage(maskCanvas, 0, 0);
      compositeCanvasContext.filter = "none";
      compositeCanvasContext.globalCompositeOperation = "source-in";
      compositeCanvasContext.filter = "brightness(120%) saturate(110%)";
      compositeCanvasContext.drawImage(inputVideo, 0, 0);
      compositeCanvasContext.filter = "none";

      const outputCanvas = new OffscreenCanvas(
        inputVideo.videoWidth,
        inputVideo.videoHeight
      );
      const outputCanvasContext = outputCanvas.getContext("2d", {
        alpha: true,
      });
      const gradientCanvas = drawBackgroundGradient(
        backgroundGradientName,
        outputCanvas.width,
        outputCanvas.height,
      );
      outputCanvasContext.drawImage(gradientCanvas, 0, 0);
      outputCanvasContext.drawImage(compositeCanvas, 0, 0);
      // await bodySegmentation.(
      //   outputCanvas,
      //   inputVideo,
      //   offscreenCanvasContext.getImageData(
      //     0,
      //     0,
      //     offscreenCanvas.width,
      //     offscreenCanvas.height
      //   ),
      //   1,
      //   3,
      //   false
      // );
      return outputCanvas;
    } else logError("segmenter.segmentPeople not ready..");
  } catch (e) {
    logError("replaceCameraBackground", {}, e);
    segmenter = null;
    segmenterPromise = null;
  }
};

export const drawBackgroundGradient = (
  backgroundGradientName,
  width,
  height
) => {
  const gradientCanvas = new OffscreenCanvas(width, height);
  const gradientCanvasContext = gradientCanvas.getContext("2d", {
    alpha: true,
  });
  const backgroundGradient = backgroundGradientList[backgroundGradientName];
  const gradient = gradientCanvasContext.createLinearGradient(
    0,
    0,
    0,
    gradientCanvas.height
  );
  for (const colorObject of backgroundGradient.colors) {
    for (const [position, color] of Object.entries(colorObject)) {
      gradient.addColorStop(parseFloat(position), color);
    }
  }
  // Fill with gradient
  gradientCanvasContext.fillStyle = gradient;
  gradientCanvasContext.fillRect(
    0,
    0,
    gradientCanvas.width,
    gradientCanvas.height
  );
  return gradientCanvas;
};

export const drawCameraBubble = async (
  cameraVideo,
  width,
  height,
  blurBackground
) => {
  const outputCanvas = new OffscreenCanvas(width, height);
  //1. Draw and crop video inside canvas:
  const cameraDimensions = getCameraFillDimensions(
    cameraVideo.videoWidth,
    cameraVideo.videoHeight,
    1
  );
  const cameraCanvas = new OffscreenCanvas(
    cameraDimensions.width,
    cameraDimensions.height
  );
  const cameraCanvasContext = cameraCanvas.getContext("2d", {
    alpha: true,
  });

  //Clipping path:
  cameraCanvasContext.clearRect(
    0,
    0,
    cameraDimensions.width,
    cameraDimensions.height
  );
  const videoRadius = cameraDimensions.height / 2;
  cameraCanvasContext.beginPath();
  cameraCanvasContext.arc(
    cameraDimensions.width / 2,
    cameraDimensions.height / 2,
    videoRadius,
    0,
    2 * Math.PI
  );
  cameraCanvasContext.clip();
  
  let sourceVideo = cameraVideo;
  if (blurBackground) {
    const blurCanvas = await blurCameraBackground(cameraVideo);
    sourceVideo = blurCanvas;
  }
  cameraCanvasContext.drawImage(
    sourceVideo,
    cameraDimensions.x,
    cameraDimensions.y,
    cameraDimensions.width,
    cameraDimensions.height,
    0,
    0,
    cameraDimensions.width,
    cameraDimensions.height
  );
  //2. Draw white outline
  const outputCanvasContext = outputCanvas.getContext("2d", {
    alpha: true,
  });
  outputCanvasContext.fillStyle = "white";
  outputCanvasContext.beginPath();
  const radius = outputCanvas.height / 2 + 2;
  outputCanvasContext.arc(
    outputCanvas.width / 2,
    outputCanvas.height / 2,
    radius,
    0,
    2 * Math.PI
  );
  outputCanvasContext.fill();
  outputCanvasContext.drawImage(cameraCanvas, 0, 0);
  return outputCanvas;
};

export const getCameraFillDimensions = (sW, sH, aspectRatio) => {
  const tooWide = sW / sH > aspectRatio;
  const width = tooWide ? sH * aspectRatio : sW;
  const height = tooWide ? sH : sW / aspectRatio;
  const x = (sW - width) / 2;
  const y = (sH - height) / 2;
  return { x, y, width, height, tooWide };
};
